import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '@/utils/api';

/**
 * ActiveBusinessPicker — Round 36
 *
 * Admin-only dropdown (shown in Dashboard's Internal Tools block).
 * Lets founder/admin users switch between multiple business profiles
 * or create a new one — all hidden from regular Starter/Growth/Pro
 * users by the caller's visibility gate.
 *
 * Calls:
 *   GET  /api/admin/business/list
 *   POST /api/admin/business/switch
 *   POST /api/admin/business/create
 */
export const ActiveBusinessPicker = ({ onSwitch }) => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [newName, setNewName] = useState('');
  const [dupName, setDupName] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/business/list');
      setProfiles(res.data?.profiles || []);
    } catch (e) {
      toast.error("Couldn't load business profiles");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const active = profiles.find(p => p.is_active);

  const handleSwitch = async (id) => {
    if (!id || id === '__new__') return;
    try {
      const res = await api.post('/admin/business/switch', { profile_id: id });
      toast.success(`Switched to ${res.data?.active?.business_name || 'profile'}`);
      await load();
      onSwitch?.(res.data?.active);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Switch failed');
    }
  };

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return toast.error('Enter a business name');
    try {
      const res = await api.post('/admin/business/create', {
        business_name: name, make_active: true,
      });
      toast.success(`Created ${res.data?.profile?.business_name}`);
      setNewName('');
      setCreating(false);
      // Round 218B P0 (2026-02-07) — a freshly created business has
      // no BBB plan, no Brand Brain, no offer. Route explicitly to
      // /dashboard?openPlan=1 so Business Builder Bot opens for the
      // new active business (a plain reload skips the auto-open for
      // internal users because their subscription_status=active).
      setTimeout(() => {
        window.location.href = '/dashboard?openPlan=1';
      }, 300);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed');
    }
  };

  const handleDuplicate = async () => {
    if (!active) return toast.error('No active business to duplicate');
    try {
      const res = await api.post('/admin/business/duplicate', {
        source_profile_id: active.id,
        new_name: dupName.trim() || undefined,
        make_active: true,
      });
      toast.success(`Duplicated → ${res.data?.profile?.business_name}`);
      setDupName('');
      setDuplicating(false);
      await load();
      onSwitch?.(res.data?.profile);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Duplicate failed');
    }
  };

  return (
    <div
      data-testid="active-business-picker"
      className="flex flex-wrap items-center gap-2 bg-slate-900/40 rounded-lg px-3 py-2 border border-amber-500/20"
    >
      <span
        data-testid="active-business-label"
        className="flex items-center gap-1.5 text-xs font-semibold text-amber-300"
      >
        <span aria-hidden="true">💼</span>
        <span>Active Business:</span>
      </span>
      <select
        data-testid="active-business-select"
        disabled={loading}
        value={active?.id || ''}
        onChange={(e) => {
          if (e.target.value === '__new__') {
            setCreating(true);
            setDuplicating(false);
            return;
          }
          if (e.target.value === '__dup__') {
            setDuplicating(true);
            setCreating(false);
            setDupName(active ? `${active.business_name} (copy)` : '');
            return;
          }
          handleSwitch(e.target.value);
        }}
        className="bg-slate-900 border border-amber-500/40 text-amber-200 text-xs rounded px-2 py-1 min-w-[140px]"
      >
        {profiles.map(p => (
          <option key={p.id} value={p.id}>{p.business_name || '(untitled)'}</option>
        ))}
        <option value="__new__">+ Create new…</option>
        {active && (
          <option value="__dup__">⎘ Duplicate current…</option>
        )}
      </select>
      {creating && (
        <div className="flex items-center gap-1" data-testid="active-business-create-row">
          <input
            data-testid="active-business-create-input"
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New business name"
            className="bg-slate-900 border border-amber-500/40 text-xs text-amber-100 rounded px-2 py-1 w-40"
          />
          <button
            data-testid="active-business-create-btn"
            onClick={handleCreate}
            className="px-2 py-1 bg-amber-500 hover:bg-amber-600 text-white text-xs rounded font-medium"
          >
            Create
          </button>
          <button
            onClick={() => { setCreating(false); setNewName(''); }}
            className="px-2 py-1 text-amber-300 hover:text-amber-100 text-xs"
          >
            Cancel
          </button>
        </div>
      )}
      {duplicating && (
        <div className="flex items-center gap-1" data-testid="active-business-duplicate-row">
          <input
            data-testid="active-business-duplicate-input"
            type="text"
            value={dupName}
            onChange={(e) => setDupName(e.target.value)}
            placeholder="Copy name (optional)"
            className="bg-slate-900 border border-amber-500/40 text-xs text-amber-100 rounded px-2 py-1 w-48"
          />
          <button
            data-testid="active-business-duplicate-btn"
            onClick={handleDuplicate}
            className="px-2 py-1 bg-emerald-500 hover:bg-emerald-600 text-white text-xs rounded font-medium"
          >
            Duplicate
          </button>
          <button
            onClick={() => { setDuplicating(false); setDupName(''); }}
            className="px-2 py-1 text-amber-300 hover:text-amber-100 text-xs"
          >
            Cancel
          </button>
        </div>
      )}
      <span className="text-[10px] text-amber-300/60">
        ({profiles.length} profile{profiles.length === 1 ? '' : 's'})
      </span>
      {active?.is_founder_identity && (
        <span
          data-testid="founder-brain-active-pill"
          title="Founder philosophy and brand voice are currently active."
          className="ml-1 inline-flex items-center gap-1.5 rounded-full border border-fuchsia-400/40 bg-fuchsia-500/10 px-2.5 py-0.5 text-[11px] font-semibold text-fuchsia-200"
        >
          <span aria-hidden="true" className="text-fuchsia-300">✦</span>
          <span>Active: PayGBot Founder Brain</span>
        </span>
      )}
    </div>
  );
};

export default ActiveBusinessPicker;
